import type { MouseEventHandler } from 'react'
import React from 'react'
import PropTypes from 'prop-types'
import { sanitizeProps } from '@fs/zion-frontend-friends'
import { useTranslation } from 'react-i18next'
import Row from '../row'
import InputChip, { propTypes as inputChipPropTypes } from './InputChip'

function makeAriaLabel(t, isSelectable, ariaChipDescription, chipName): string {
  if (isSelectable && ariaChipDescription) {
    return t('dismiss-input-chip.selectable.with-description.aria-label', 'Remove {chipName} {ariaChipDescription}', {
      chipName,
      ariaChipDescription,
    })
  }
  if (isSelectable) {
    return t('dismiss-input-chip.selectable.aria-label', 'Remove {chipName} checkbox', { chipName })
  }
  if (ariaChipDescription) {
    return t(
      'dismiss-input-chip.not-selectable.with-description.aria-label',
      'Dismiss {chipName} {ariaChipDescription}',
      {
        chipName,
        ariaChipDescription,
      }
    )
  }
  return t('dismiss-input-chip.not-selectable.aria-label', 'Dismiss {chipName}', { chipName })
}

type ChipListProps = PropTypes.InferProps<typeof chipListPropTypes> &
  Partial<{
    isDismissible: boolean
    onDismiss: MouseEventHandler
  }>

const noOp = (): void => undefined

function ChipList({
  ariaChipDescription,
  chipCollection,
  isDismissible,
  isSelectable,
  linkName,
  onClick = noOp,
  onDismiss,
  ...props
}: ChipListProps): JSX.Element {
  // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
  const { chips, selectChip, removeChip, debounce = 200 } = chipCollection
  const [t] = useTranslation()

  function handleDismiss(e, chip): void {
    e.preventDefault()
    e.stopPropagation()
    removeChip(chip)
    if (onDismiss) onDismiss(chip)
  }

  function handleClick(e, chip): void {
    selectChip(chip)
    // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
    onClick(e, chip)
  }

  return (
    <Row wrap gutters="xxs" {...sanitizeProps(props)}>
      {chips.map((chip) => {
        const { value, 'aria-label': ariaLabel, dismissAriaLabel, ...chipProps } = chip
        return (
          <InputChip
            {...sanitizeProps(chipProps)}
            aria-label={ariaLabel}
            debounce={debounce} // this should be the same as useChipCollection debounce
            dismissAriaLabel={
              dismissAriaLabel || makeAriaLabel(t, isSelectable, ariaChipDescription, ariaLabel || value)
            }
            key={value}
            linkName={chip.linkName || linkName}
            // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
            onClick={isSelectable ? (e) => handleClick(e, chip) : (e) => onClick(e, chip)}
            onDismiss={isDismissible ? (e) => handleDismiss(e, chip) : undefined}
            role={isSelectable ? 'checkbox' : undefined}
            selected={isSelectable && chip.selected}
            value={value}
          />
        )
      })}
    </Row>
  )
}

// @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
type InputChipsProps = PropTypes.InferProps<typeof InputChips.propTypes>

// The difference between FilterChips and InputChips is that Filter Chips cannot be dismissed, and input chips are always dismissible.
// InputChips can have onClick as well
function InputChips({ chipCollection, isSelectable, onClick, onDismiss, ...props }: InputChipsProps): JSX.Element {
  return (
    <ChipList
      {...sanitizeProps(props)}
      chipCollection={chipCollection}
      isDismissible
      isSelectable={isSelectable}
      onClick={onClick}
      // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
      onDismiss={onDismiss}
    />
  )
}

// @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
type FilterChipsProps = PropTypes.InferProps<typeof FilterChips.propTypes>
function FilterChips({ chipCollection, ...props }: FilterChipsProps): JSX.Element {
  return <ChipList {...sanitizeProps(props)} chipCollection={chipCollection} isSelectable isDismissible={false} />
}

const inputChipShape = PropTypes.shape(inputChipPropTypes)

const chipCollectionShape = PropTypes.shape({
  chips: PropTypes.arrayOf(inputChipShape).isRequired,
  addChip: PropTypes.func.isRequired,
  removeChip: PropTypes.func.isRequired,
  selectedChips: PropTypes.arrayOf(inputChipShape),
  selectChip: PropTypes.func,
  debounce: PropTypes.number,
})

const chipListPropTypes = {
  /** The collection of chips for this component. Use the useChipCollection hook to create a chip collection. */
  chipCollection: chipCollectionShape,

  /** Added to the aria-label to help with accessibility. Example: If `Topic` is specified and the chip is selectable with a value of `World War 2` the dismiss button will read as `Remove World War 2 Topic` */
  ariaChipDescription: PropTypes.string,

  /** Specify that your chips can be selected */
  isSelectable: PropTypes.bool,

  /** Optional. Tracking name to log when a tab is clicked (overridden by individual chips with a linkName prop) */
  linkName: PropTypes.string,

  /** A function that is triggered onClick. The chip passes the event to the onClick function */
  onClick: PropTypes.func,

  /** A function that is triggered when a chip is dismissed */
  onDismiss: PropTypes.func,
}

ChipList.propTypes = chipListPropTypes
FilterChips.propTypes = chipListPropTypes
InputChips.propTypes = chipListPropTypes

export { FilterChips, InputChips }
